"use client";

import React, { useState, useEffect } from "react";
import { useTheme } from "next-themes";

// Styles
import * as S from "./styles";

// Components
import {
  DropdownProfile,
  Input,
  FormGroup,
  Modal,
  LanguageSwitcher,
  Notification,
  ThemeButton,
  InputGroup,
} from "@/components";
import { default as ButtonComponent } from "@/components/button";

// Types
import { DictionaryProps } from "@/types";

// Images
//import LogoNight from '@/assets/images/logo.png';
//import LogoDay from '@/assets/images/logoDay.png';
import LogoNight from "@/assets/images/logoDark.png";
import LogoDay from "@/assets/images/logoLight.png";
import { useAppUI } from "@/context/AppUIContext";
import { useRouter } from "next/navigation";

// Hooks
import { useModal, useUser } from "@/hooks";

const HeaderLoggedOut: React.FC<
  DictionaryProps & { showAuthButtons?: boolean }
> = ({ dict, showAuthButtons = false }) => {
  const router = useRouter();
  const { theme, setTheme } = useTheme();
  const { user } = useUser();

  const Logo = theme === "dark" ? LogoNight : LogoDay;

  const [search, setSearch] = useState("");
  const { sidebarOpen } = useAppUI();

  // Modal state moved here
  const { isOpen: isOpenEditProfile, toggleModal: toggleEditProfileModal } =
    useModal();
  const [openModalProfile, setOpenModalProfile] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    cpf: "",
    password: "",
    confirmPassword: "",
    address: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Carrega dados do usuário da sessão quando disponível
  useEffect(() => {
    if (user && user.name !== "Usuário") {
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        cpf: user.cpf || "",
        address: user.address || "",
      }));
    }
  }, [user.name, user.email, user.phone, user.cpf, user.address]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | { target: { name: string; value: any } },
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    const newErrors: any = {};
    if (!formData.name) newErrors.name = "Nome obrigatório";
    if (!formData.email) newErrors.email = "Email obrigatório";
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Senhas não coincidem";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    // Simula envio
    console.log("Perfil atualizado:", formData);
    setIsSubmitting(false);
    setOpenModalProfile(false);
  };

  return (
    <S.HeaderPage $isOpen={sidebarOpen}>
      <S.LogoHeader src={Logo.src} />
      <S.BoxLastItemsHeader>
        <ThemeButton
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          activeTheme={theme === "light"}
        />
        <LanguageSwitcher />
        {/* <Notification /> */}
        {/* <DropdownProfile onOpenModal={() => toggleEditProfileModal()} /> */}

        {showAuthButtons && (
          <>
            <button
              onClick={() => router.push(`/register`)}
              className="hidden sm:inline-flex text-xs cursor-pointer bg-gray-200 hover:bg-gray-300 text-black font-bold py-1.5 px-4 rounded-3xl  duration-300  ease-in-out"
            >
              {dict["Create new account"] || dict["Register"] || "Register"}
            </button>
            <button
              onClick={() => router.push(`/login`)}
              className="text-xs cursor-pointer bg-blue-500 hover:bg-blue-700 text-white font-bold py-1.5 px-4 rounded-3xl  duration-300  ease-in-out"
            >
              {dict["Sign in"] || dict["Log in"] || "Log in"}
            </button>
          </>
        )}
      </S.BoxLastItemsHeader>

      <Modal
        titleModal="Editar perfil"
        open={isOpenEditProfile}
        onOpenChange={toggleEditProfileModal}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 2 colunas: Nome e Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup>
              <Input
                name="name"
                label="Nome"
                placeholder="Claudio"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && (
                <p className="text-red-500 text-sm">{errors.name}</p>
              )}
            </FormGroup>
            <FormGroup>
              <Input
                name="email"
                label="Email"
                placeholder="teste@XOctagonIcon.com.br"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email}</p>
              )}
            </FormGroup>
          </div>

          {/* 2 colunas: Celular e CPF */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup>
              <Input
                name="phone"
                label="Celular"
                placeholder="(00) 00000-0000"
                value={formData.phone}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Input
                name="cpf"
                label="CPF"
                placeholder="444.444.444-44"
                value={formData.cpf}
                onChange={handleChange}
              />
            </FormGroup>
          </div>

          {/* 2 colunas: Senha e Confirmar Senha */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup>
              <Input
                type="password"
                name="password"
                label="Senha"
                placeholder="Senha"
                value={formData.password}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Input
                type="password"
                name="confirmPassword"
                label="Confirmar Senha"
                placeholder="Confirmar Senha"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm">{errors.confirmPassword}</p>
              )}
            </FormGroup>
          </div>

          {/* Linha única: Endereço */}
          <FormGroup>
            <Input
              name="address"
              label="Endereço"
              placeholder="Rua X Número 1 Bairro xxx"
              value={formData.address}
              onChange={handleChange}
            />
          </FormGroup>

          <ButtonComponent
            type="submit"
            disabled={isSubmitting}
            text={isSubmitting ? "Salvando..." : "Salvar"}
            variant="success"
            widthFull
          />
        </form>
      </Modal>
    </S.HeaderPage>
  );
};

export default HeaderLoggedOut;
